import type { CrmMessages } from "./englishCrmMessages";

export const frenchCrmMessages = {
  resources: {
    companies: {
      name: "Entreprise |||| Entreprises",
      forcedCaseName: "Entreprise",
      fields: {
        name: "Nom de l'entreprise",
        website: "Site web",
        linkedin_url: "LinkedIn",
        phone_number: "Numéro de téléphone",
        created_at: "Date de création",
        nb_contacts: "Nombre de contacts",
        revenue: "Chiffre d'affaires",
        sector: "Segment",
        segments: "Segments",
        size: "Taille",
        tax_identifier: "Identifiant fiscal",
        address: "Adresse",
        city: "Ville",
        zipcode: "Code postal",
        state_abbr: "État",
        country: "Pays",
        description: "Description",
        context_links: "URLs de contexte",
        sales_id: "Responsable de compte",
      },
      empty: {
        description: "Il semble que la liste de vos entreprises soit vide.",
        title: "Aucune entreprise trouvée",
        bc_sync_description:
          "Les clients sont synchronisés depuis Business Central. Consultez Paramètres → Synchronisation BC ou attendez la synchronisation nocturne.",
      },
      field_categories: {
        contact: "Contact",
        additional_info: "Informations supplémentaires",
        address: "Adresse",
        context: "Contexte",
      },
      action: {
        create: "Créer une entreprise",
        edit: "Modifier l'entreprise",
        new: "Nouvelle entreprise",
        show: "Afficher l'entreprise",
      },
      added_on: "Ajoutée le %{date}",
      followed_by: "Suivie par %{name}",
      followed_by_you: "Suivie par vous",
      no_contacts: "Aucun contact",
      nb_contacts: "%{smart_count} contact |||| %{smart_count} contacts",
      nb_deals: "%{smart_count} affaire |||| %{smart_count} affaires",
      sizes: {
        one_employee: "1 employé",
        two_to_nine_employees: "2-9 employés",
        ten_to_forty_nine_employees: "10-49 employés",
        fifty_to_two_hundred_forty_nine_employees: "50-249 employés",
        two_hundred_fifty_or_more_employees: "250 employés ou plus",
      },
      autocomplete: {
        create_error:
          "Une erreur s'est produite lors de la création de l'entreprise",
        create_item: "Créer %{item}",
        create_label: "Commencez à taper pour créer une nouvelle entreprise",
      },
      filters: {
        only_mine: "Seulement les entreprises que je gère",
      },
    },
    campaigns: {
      name: "Campagne |||| Campagnes",
      fields: {
        name: "Nom",
        description: "Description",
        status: "Statut",
        product_name: "Produit",
        kind: "Type de tâche",
        title: "Titre",
        instructions: "Instructions",
        assignee: "Assigné à",
        targets: "Cibles",
      },
      tabs: {
        overview: "Vue d'ensemble",
        definitions: "Définitions de tâches",
        my_tasks: "Mes tâches",
      },
      task_definitions: {
        add: "Ajouter une définition de tâche",
        edit: "Modifier la définition de tâche",
        empty: "Aucune définition de tâche pour l'instant.",
        targets: "cibles",
        generated: "Généré",
      },
      targets: {
        add: "Ajouter une cible",
        required: "Ajoutez au moins une cible",
        empty: "Aucune cible de campagne configurée.",
        campaign_required:
          "Ajoutez des cibles de campagne avant d'utiliser l'héritage",
        mode: "Mode de ciblage",
        inherit: "Hériter des cibles de la campagne",
        custom: "Utiliser des cibles personnalisées pour cette définition",
        inherited_badge: "Hérité de la campagne",
        select_segment: "Sélectionner un segment…",
        select_company: "Sélectionner une entreprise…",
        select_contact: "Sélectionner un contact…",
        select_tag: "Sélectionner une étiquette…",
        types: {
          segment: "Segment BC",
          company: "Entreprise",
          contact: "Contact",
          tag: "Étiquette",
        },
      },
      assignee: {
        required: "Sélectionnez un assigné",
      },
      generate: {
        action: "Générer les tâches",
        reconcile: "Réconcilier les tâches",
        confirm: "Générer",
        fallback: "Commercial de secours",
        fallback_help:
          "Sélectionnez un commercial de secours pour les cibles sans commercial assigné.",
        success: "Tâches générées",
        created: "Créées",
        skipped: "Ignorées",
        deleted: "Supprimées",
        missing: "Ignorées (pas de commercial)",
      },
      complete_note: {
        title: "Ajouter une note à la tâche",
        placeholder: "Rédigez une note sur cette visite ou cet appel…",
        save: "Enregistrer la note",
        skip: "Terminer sans note",
        success: "Tâche terminée",
        saved_to_contact: "La note sera enregistrée sur le contact : %{name}",
        saved_to_company: "La note sera enregistrée sur l'entreprise : %{name}",
      },
      status: {
        draft: "Brouillon",
        active: "Active",
        completed: "Terminée",
        overdue: "En retard",
        open: "Ouverte",
        snoozed: "Reportée",
      },
      progress: {
        label: "Progression",
        overdue_count: "%{count} en retard",
      },
      open_tasks: {
        title: "Tâches",
        show_completed: "Afficher les terminées",
        all_completed: "Toutes les tâches sont terminées.",
        campaign_task: "Tâche de campagne",
        company_fallback: "Entreprise n°%{id}",
        contact_fallback: "Contact n°%{id}",
      },
      kinds: {
        visit_demo: "Visite et démo",
        phone_call: "Appel téléphonique",
        send_samples: "Envoyer des échantillons",
        send_info: "Envoyer des informations produit",
        meeting: "Réunion",
        follow_up: "Relance",
        newsletter: "Envoyer une newsletter",
        personal_task: "Tâche personnelle",
      },
      metadata: {
        product_to_demo: "Produit à démontrer",
        talk_points: "Points à aborder",
        sample_description: "Description de l'échantillon",
        materials: "Documents à envoyer",
        meeting_format: "Format de réunion (in_person ou online)",
        newsletter_subject: "Objet de la newsletter",
        notes: "Notes",
      },
    },
    contacts: {
      name: "Contact |||| Contacts",
      forcedCaseName: "Contact",
      field_categories: {
        background_info: "Informations complémentaires",
        identity: "Identité",
        misc: "Divers",
        personal_info: "Informations personnelles",
        position: "Poste",
      },
      fields: {
        first_name: "Prénom",
        last_name: "Nom",
        last_seen: "Dernière activité",
        title: "Titre",
        company_id: "Entreprise",
        email_jsonb: "Adresses e-mail",
        email: "E-mail",
        phone_jsonb: "Numéros de téléphone",
        phone_number: "Numéro de téléphone",
        linkedin_url: "URL LinkedIn",
        background: "Informations de contexte",
        has_newsletter: "Abonné à la newsletter",
        sales_id: "Responsable de compte",
      },
      action: {
        add: "Ajouter un contact",
        add_first: "Ajoutez votre premier contact",
        browse_synced: "Parcourir les contacts synchronisés",
        create: "Créer un contact",
        edit: "Modifier le contact",
        export_vcard: "Exporter en vCard",
        new: "Nouveau contact",
        show: "Afficher le contact",
      },
      background: {
        last_activity_on: "Dernière activité le %{date}",
        added_on: "Ajouté le %{date}",
        followed_by: "Suivi par %{name}",
        followed_by_you: "Suivi par vous",
        status_none: "Aucun",
      },
      position_at: "%{title} chez",
      position_at_company: "%{title} chez %{company}",
      part_of_company: "Fait partie de %{name}",
      open_company: "Ouvrir l'entreprise",
      empty: {
        description: "Il semble que votre liste de contacts soit vide.",
        title: "Aucun contact trouvé",
        bc_sync_description:
          "Les contacts sont synchronisés depuis Business Central. Consultez Paramètres → Synchronisation BC ou attendez la synchronisation nocturne.",
      },
      import: {
        title: "Importer des contacts",
        button: "Importer un fichier CSV",
        complete:
          "Import des contacts terminé. %{importCount} contacts importés, %{errorCount} erreurs",
        progress:
          "%{importCount} / %{rowCount} contacts importés, avec %{errorCount} erreurs.",
        error:
          "Échec de l'importation de ce fichier. Veuillez vous assurer que vous avez fourni un fichier CSV valide.",
        imported: "Importé",
        remaining_time: "Temps restant estimé :",
        running: "L'import est en cours, merci de ne pas fermer cet onglet.",
        sample_download: "Télécharger un exemple CSV",
        sample_hint:
          "Voici un exemple de fichier CSV que vous pouvez utiliser comme modèle",
        stop: "Arrêter l'importation",
        csv_file: "Fichier CSV",
        contacts_label: "contact |||| contacts",
      },
      inputs: {
        genders: {
          male: "Monsieur",
          female: "Madame",
          nonbinary: "Indéterminé",
        },
        personal_info_types: {
          work: "Pro",
          home: "Perso",
          other: "Autre",
        },
      },
      list: {
        error_loading: "Erreur lors du chargement des contacts",
      },
      bulk_tag: {
        action: "Étiqueter",
        back: "Retour aux étiquettes",
        create_description:
          "Créez une nouvelle étiquette et appliquez-la aux contacts sélectionnés.",
        description:
          "Choisissez une étiquette existante ou créez-en une pour les contacts sélectionnés.",
        empty:
          "Aucune étiquette pour le moment. Créez-en une pour étiqueter les contacts sélectionnés.",
        error: "Impossible d'ajouter l'étiquette aux contacts",
        noop: "Les contacts sélectionnés ont déjà cette étiquette",
        success:
          "Étiquette ajoutée à %{smart_count} contact |||| Étiquette ajoutée à %{smart_count} contacts",
        title: "Ajouter une étiquette aux contacts",
      },
      merge: {
        action: "Fusionner avec un autre contact",
        confirm: "Fusionner les contacts",
        current_contact: "Contact actuel (sera supprimé)",
        description: "Fusionnez ce contact avec un autre.",
        error: "Échec de la fusion des contacts",
        merging: "Fusion...",
        no_additional_data: "Aucune donnée supplémentaire à fusionner",
        select_target: "Veuillez sélectionner un contact avec lequel fusionner",
        success: "Contacts fusionnés avec succès",
        target_contact: "Contact cible (sera conservé)",
        title: "Fusionner les contacts",
        warning_description:
          "Toutes les données seront transférées au deuxième contact. Cette action ne peut pas être annulée.",
        warning_title: "Avertissement : opération destructrice",
        what_will_be_merged: "Ce qui sera fusionné :",
      },
      filters: {
        before_last_month: "Avant le mois dernier",
        before_this_month: "Avant ce mois-ci",
        before_this_week: "Avant cette semaine",
        managed_by_me: "Géré par moi",
        search: "Rechercher nom, entreprise...",
        this_week: "Cette semaine",
        today: "Aujourd'hui",
        tags: "Étiquettes",
        tasks: "Tâches",
      },
      hot: {
        empty_change_status:
          'Changez le statut d\'un contact en ajoutant une note à ce contact et en cliquant sur "afficher les options".',
        empty_hint: 'Les contacts avec un statut "chaud" apparaîtront ici.',
        title: "Contacts chauds",
      },
    },
    deals: {
      name: "Affaire |||| Affaires",
      fields: {
        name: "Nom",
        description: "Description",
        company_id: "Entreprise",
        contact_ids: "Contacts",
        category: "Catégorie",
        amount: "Budget",
        expected_closing_date: "Date de clôture prévue",
        stage: "Étape",
      },
      action: {
        back_to_deal: "Retour à l'affaire",
        create: "Créer une affaire",
        new: "Nouvelle affaire",
      },
      field_categories: {
        misc: "Divers",
      },
      archived: {
        action: "Archiver",
        error: "Erreur : affaire non archivée",
        list_title: "Affaires archivées",
        success: "Affaire archivée",
        title: "Affaire archivée",
        view: "Afficher les affaires archivées",
      },
      inputs: {
        linked_to: "Lié à",
      },
      unarchived: {
        action: "Renvoyer au tableau",
        error: "Erreur : affaire non désarchivée",
        success: "Affaire désarchivée",
      },
      updated: "Affaire mise à jour",
      empty: {
        before_create: "avant de créer une affaire.",
        description: "Il semble que votre liste d'affaires soit vide.",
        title: "Aucune affaire trouvée",
      },
      invalid_date: "Date invalide",
    },
    notes: {
      name: "Note |||| Notes",
      forcedCaseName: "Note",
      fields: {
        status: "Statut",
        date: "Date",
        attachments: "Pièces jointes",
        contact_id: "Contact",
        deal_id: "Affaire",
      },
      action: {
        add: "Ajouter une note",
        add_first: "Ajoutez votre première note",
        delete: "Supprimer la note",
        edit: "Modifier la note",
        update: "Mettre à jour la note",
        add_this: "Ajouter cette note",
      },
      sheet: {
        create: "Créer une note",
        create_for: "Créer une note pour %{name}",
        edit: "Modifier la note",
        edit_for: "Modifier la note pour %{name}",
      },
      deleted: "Note supprimée",
      empty: "Aucune note pour l'instant",
      author_added: "%{name} a ajouté une note",
      you_added: "Vous avez ajouté une note",
      me: "Moi",
      list: {
        error_loading: "Erreur lors du chargement des notes",
      },
      note_for_contact: "Note pour %{name}",
      stepper: {
        hint: "Accédez à une page de contact et ajoutez une note",
      },
      added: "Note ajoutée",
      inputs: {
        add_note: "Ajouter une note",
        options_hint: "(joindre des fichiers ou modifier les détails)",
        show_options: "Afficher les options",
      },
      actions: {
        attach_document: "Joindre un document",
      },
      validation: {
        note_or_attachment_required: "Une note ou une pièce jointe est requise",
      },
    },
    sales: {
      name: "Utilisateur |||| Utilisateurs",
      fields: {
        first_name: "Prénom",
        last_name: "Nom",
        email: "E-mail",
        administrator: "Admin",
        disabled: "Désactivé",
        role: "Rôle",
      },
      roles: {
        admin: "Administrateur",
        manager: "Manager",
        user: "Utilisateur",
      },
      create: {
        error:
          "Une erreur s'est produite lors de la création de l'utilisateur.",
        success:
          "Utilisateur créé. Ils recevront prochainement un email pour définir leur mot de passe.",
        title: "Créer un nouvel utilisateur",
      },
      edit: {
        error: "Une erreur s'est produite. Veuillez réessayer.",
        record_not_found: "Enregistrement introuvable",
        success: "Utilisateur mis à jour avec succès",
        title: "Modifier %{name}",
      },
      action: {
        new: "Nouvel utilisateur",
      },
    },
    tasks: {
      name: "Tâche |||| Tâches",
      forcedCaseName: "Tâche",
      fields: {
        text: "Description",
        due_date: "Date d'échéance",
        type: "Type",
        contact_id: "Contact",
        sales_id: "Assigné à",
        due_short: "échéance",
      },
      action: {
        add: "Ajouter une tâche",
        create: "Créer une tâche",
        edit: "Modifier la tâche",
      },
      actions: {
        postpone_next_week: "Reporté à la semaine prochaine",
        postpone_tomorrow: "Reporter à demain",
        title: "Actions de tâche",
      },
      added: "Tâche ajoutée",
      deleted: "Tâche supprimée avec succès",
      dialog: {
        create: "Créer une tâche",
        create_for: "Créer une tâche pour %{name}",
      },
      sheet: {
        edit: "Modifier la tâche",
        edit_for: "Modifier la tâche pour %{name}",
      },
      empty: "Aucune tâche pour l'instant",
      empty_list_hint: "Les tâches ajoutées à vos contacts apparaîtront ici.",
      filters: {
        later: "Plus tard",
        overdue: "En retard",
        this_week: "Cette semaine",
        today: "Aujourd'hui",
        tomorrow: "Demain",
        with_pending: "Avec des tâches en attente",
        campaign_tasks: "Tâches de campagne uniquement",
      },
      regarding_contact: "(Concernant : %{name})",
      regarding_company: "(Concernant : %{name})",
      assigned_to: "Assigné à : %{name}",
      updated: "Tâche mise à jour",
      recurrence: {
        badge: "Récurrente",
        enabled: "Tâche récurrente",
        every_day: "Chaque jour",
        every_week: "Chaque semaine",
        every_month: "Chaque mois",
        every_year: "Chaque année",
        custom: "Personnalisé",
        every_count: "Intervalle",
        every_count_help:
          "Utilisez 1 pour chaque intervalle, ou une valeur supérieure pour toutes les N périodes.",
        every_unit: "Unité",
        days: "Jours",
        weeks: "Semaines",
        months: "Mois",
        years: "Années",
        has_end_date: "Définir une date de fin",
        end_date: "Date de fin",
      },
    },
    tags: {
      name: "Étiquette |||| Étiquettes",
      action: {
        add: "Ajouter une étiquette",
        create: "Créer une nouvelle étiquette",
      },
      dialog: {
        color: "Couleur",
        create_title: "Créer une nouvelle étiquette",
        edit_title: "Modifier l'étiquette",
        name_label: "Nom de l'étiquette",
        name_placeholder: "Saisir le nom de l'étiquette",
      },
    },
  },
  crm: {
    action: {
      reset_password: "Réinitialiser le mot de passe",
    },
    auth: {
      first_name: "Prénom",
      last_name: "Nom",
      confirm_password: "Confirmer le mot de passe",
      confirmation_required:
        "Veuillez suivre le lien que nous venons de vous envoyer par email pour confirmer votre compte.",
      recovery_email_sent:
        "Si vous êtes un utilisateur enregistré, vous devriez recevoir prochainement un e-mail de récupération de mot de passe.",
      sign_in_failed: "Échec de la connexion.",
      sign_in_google_workspace: "Connectez-vous avec Google Workplace",
      signup: {
        create_account: "Créer un compte",
        create_first_user:
          "Créez le premier compte utilisateur pour terminer la configuration.",
        creating: "Création...",
        initial_user_created: "Utilisateur initial créé avec succès",
      },
      welcome_title: "Bienvenue sur Atomic CRM",
    },
    mfa: {
      setup: {
        title: "Configurer l'authentification à deux facteurs",
        subtitle:
          "L'authentification à deux facteurs est requise avant d'utiliser Gobo CRM.",
        choose_totp: "Application d'authentification (recommandé)",
        choose_sms: "SMS",
        backup_hint:
          "Astuce : ajoutez une application de secours dans votre profil.",
      },
      verify: {
        title: "Vérifiez votre identité",
        subtitle: "Saisissez le code de votre application ou SMS.",
        submit: "Vérifier",
      },
      set_password: {
        title: "Vérifiez avant de changer le mot de passe",
        subtitle:
          "Saisissez votre code à deux facteurs pour continuer. Requis car MFA est activé sur votre compte.",
      },
      settings: {
        title: "Authentification à deux facteurs",
        description:
          "Gérez vos facteurs MFA. Nous recommandons une application de secours.",
        none: "Aucun facteur MFA enregistré.",
        remove: "Supprimer",
        add_factor: "Ajouter un facteur de secours",
      },
      totp: {
        friendly_name: "Application d'authentification",
        description:
          "Utilisez Microsoft Authenticator, Google Authenticator ou une autre app TOTP.",
        start: "Configurer l'application",
        manual_secret: "Clé de saisie manuelle :",
        scan_hint:
          "Scannez le code QR, puis saisissez le code à 6 chiffres.",
      },
      sms: {
        friendly_name: "SMS",
        description:
          "Recevez des codes par SMS. Format international, ex. +4512345678.",
        phone: "Numéro de téléphone",
        send_code: "Envoyer le code",
        code_sent: "Saisissez le code envoyé sur votre téléphone.",
        code_resent: "Un nouveau code a été envoyé.",
        resend: "Renvoyer le code",
      },
      code: "Code de vérification",
      remember_device: "Mémoriser cet appareil pendant 30 jours",
      verify_and_continue: "Vérifier et continuer",
      back: "Retour",
      setup_complete: "L'authentification à deux facteurs est activée.",
      enroll_error: "Impossible de démarrer la configuration MFA.",
      verify_error: "Code invalide. Veuillez réessayer.",
      trusted_device_error:
        "MFA activée, mais cet appareil n'a pas pu être mémorisé.",
      no_factor: "Aucun facteur MFA. Configurez l'authentification à deux facteurs.",
      factor_removed: "Facteur MFA supprimé.",
      unenroll_error: "Impossible de supprimer le facteur MFA.",
      demo: {
        stub: "Mode démo : MFA simulée. Cliquez sur continuer.",
        continue: "Continuer",
      },
    },
    common: {
      activity: "Activité",
      added: "ajoutée",
      details: "Détails",
      last_activity_with_date: "dernière activité %{date}",
      load_more: "Charger plus",
      misc: "Divers",
      past: "Passé",
      read_more: "En savoir plus",
      retry: "Réessayer",
      show_less: "Afficher moins",
      task_count: "%{smart_count} tâche |||| %{smart_count} tâches",
      copied: "Copié !",
      copy: "Copier",
      loading: "Chargement...",
      me: "Moi",
    },
    activity: {
      added_company: "%{name} a ajouté l'entreprise",
      you_added_company: "Vous avez ajouté l'entreprise",
      added_contact: "%{name} a ajouté le contact",
      you_added_contact: "Vous avez ajouté le contact",
      added_note: "%{name} a ajouté une note sur",
      you_added_note: "Vous avez ajouté une note sur",
      added_note_about_deal: "%{name} a ajouté une note sur l'affaire",
      you_added_note_about_deal: "Vous avez ajouté une note sur l'affaire",
      added_company_note: "%{name} a ajouté une note sur",
      you_added_company_note: "Vous avez ajouté une note sur",
      added_deal: "%{name} a ajouté l'affaire",
      you_added_deal: "Vous avez ajouté l'affaire",
      at_company: "chez",
      to: "à",
      load_more: "Charger plus d'activité",
    },
    dashboard: {
      deals_chart: "Revenus des affaires à venir",
      deals_pipeline: "Pipeline des affaires",
      latest_activity: "Dernière activité",
      latest_activity_error:
        "Erreur lors du chargement de la dernière activité",
      latest_notes: "Mes dernières notes",
      latest_notes_added_ago: "ajouté %{timeAgo}",
      stepper: {
        install: "Installer Atomic CRM",
        progress: "%{step}/3 terminé",
        whats_next: "Et ensuite ?",
      },
      upcoming_tasks: "Tâches à venir",
    },
    header: {
      import_data: "Importer des données",
    },
    recent_website_activity: {
      title: "Activité web récente",
      empty: "Aucune activité web récente trouvée",
      loading: "Chargement...",
      demo_unavailable:
        "L'activité web récente nécessite la configuration Leadboxer et n'est pas disponible en mode démo.",
      summary:
        "%{count} visites · %{visits} sessions · %{events} pages vues (dernière)",
      match_reason: {
        domain: "Correspondance par domaine",
        domain_alias: "Correspondance par alias de domaine",
        email_domain: "Correspondance par domaine e-mail",
        linkedin: "Correspondance par LinkedIn",
        name: "Correspondance par nom",
      },
    },
    visitor_insights: {
      nav: "Aperçu visiteurs",
      title: "Aperçu visiteurs",
      description:
        "Consultez les organisations identifiées visitant vos sites web.",
      brand_title: "Aperçu visiteurs %{brand}",
      brand_description: "Données de visiteurs pour %{brand}.",
      demo_unavailable:
        "L'aperçu visiteurs nécessite la configuration Leadboxer et n'est pas disponible en mode démo.",
      loading: "Chargement...",
      loading_sessions: "Chargement des sessions...",
      empty: "Aucun visiteur trouvé pour cette période.",
      no_sessions: "Aucune session trouvée pour ce lead.",
      view_customer: "Voir le client",
      session_events: "Événements de session",
      brands: {
        gobo_dk: "Gobo DK",
        gobo_se: "Gobo SE",
        gobo_fi: "Gobo FI",
        gobo_light: "Gobo Light Solution",
        ljusdesign: "Ljusdesign",
      },
      period: {
        last_24h: "Dernières 24 heures",
        last_3d: "3 derniers jours",
        last_7d: "7 derniers jours",
        last_30d: "30 derniers jours",
      },
      columns: {
        summary: "Résumé",
        last_activity: "Dernière activité",
        industry: "Secteur",
        employees: "Employés",
        visits: "Visites",
        events: "Événements",
        last_channel: "Dernier canal",
      },
      fields: {
        industry: "Secteur",
        employees: "Employés",
        location: "Localisation",
        phone: "Téléphone",
        website: "Site web",
        score: "Score",
        tags: "Tags",
      },
    },
    email_lists: {
      nav: "Listes e-mail",
      title: "Listes e-mail",
      description:
        "Consultez et gérez les abonnements aux listes Encharge pour les marques Gobo.",
      brand_title: "Contacts %{brand}",
      brand_description: "Contacts des listes e-mail pour %{brand}.",
      demo_unavailable:
        "Les listes e-mail nécessitent la configuration Encharge et ne sont pas disponibles en mode démo.",
      loading: "Chargement...",
      loading_more: "Chargement supplémentaire...",
      empty: "Aucun contact trouvé.",
      search_placeholder: "Rechercher des contacts...",
      all_categories: "Toutes les catégories",
      create_contact: "Créer un contact",
      create_title: "Créer un contact",
      edit_title: "Modifier le contact",
      saving: "Enregistrement...",
      showing_count: "%{count} contacts dans la liste",
      exists_prompt:
        "Un contact avec cet e-mail existe déjà. Mettre à jour le contact existant ?",
      update_existing: "Mettre à jour",
      widget_title: "Listes e-mail",
      widget_no_email:
        "Ajoutez une adresse e-mail pour voir les abonnements.",
      widget_empty: "Aucun abonnement aux listes e-mail trouvé.",
      match_email: "Correspondance par e-mail",
      match_company: "Correspondance par entreprise",
      validation: {
        required_fields:
          "Le prénom, le nom et l'e-mail sont obligatoires.",
        category_required:
          "Sélectionnez au moins une catégorie de communication.",
      },
      fields: {
        email: "E-mail",
        first_name: "Prénom",
        last_name: "Nom",
        company: "Entreprise",
        phone: "Téléphone",
        postcode: "Code postal",
        contact_person: "Contact Gobo",
        categories: "Catégories de communication",
      },
      brands: {
        gobo_dk: "Gobo DK",
        gobo_se: "Gobo SE",
        gobo_fi: "Gobo FI",
        gobo_light: "Gobo Light Solution",
        ljusdesign: "Ljusdesign",
      },
    },
    image_editor: {
      change: "Changer",
      drop_hint:
        "Déposez un fichier à télécharger ou cliquez pour le sélectionner.",
      editable_content: "Contenu modifiable",
      title: "Télécharger et redimensionner l'image",
      update_image: "Mettre à jour l'image",
    },
    import: {
      action: {
        download_error_report: "Téléchargez le rapport d'erreur",
        import: "Importer",
        import_another: "Importer un autre fichier",
      },
      error: {
        unable: "Impossible d'importer ce fichier.",
      },
      idle: {
        description_1:
          "Vous pouvez importer des ventes, des entreprises, des contacts, des entreprises, des notes et des tâches.",
        description_2:
          "Les données doivent se trouver dans un fichier JSON correspondant à l'exemple suivant :",
      },
      status: {
        all_success: "Tous les enregistrements ont été importés avec succès.",
        complete: "Importation terminée.",
        failed: "Échoué",
        imported: "Importé",
        in_progress: "Import en cours, veuillez ne pas quitter cette page.",
        some_failed: "Certains enregistrements n'ont pas été importés.",
        table_caption: "Statut d'importation",
      },
      title: "Importer des données",
    },
    settings: {
      companies: {
        segments: "Segments",
      },
      dark_mode_logo: "Logo du mode sombre",
      deals: {
        categories: "Catégories",
        currency: "Devise",
        pipeline_help:
          "Sélectionnez les étapes d'affaire à considérer comme des affaires dans le pipeline.",
        pipeline_statuses: "Statuts des pipelines",
        stages: "Étapes",
      },
      light_mode_logo: "Logo du mode clair",
      notes: {
        statuses: "Statuts",
      },
      reset_defaults: "Réinitialiser aux valeurs par défaut",
      save_error: "Échec de l'enregistrement de la configuration",
      saved: "Configuration enregistrée avec succès",
      saving: "Enregistrement...",
      tasks: {
        types: "Types",
      },
      preferences: "Préférences",
      title: "Paramètres",
      app_title: "Titre de l'application",
      sections: {
        branding: "Image de marque",
        bc_sync: "Synchronisation BC",
        notifications: "Notifications",
      },
      validation: {
        duplicate: "%{display_name} en double : %{items}",
        in_use:
          "Impossible de supprimer %{display_name} encore utilisés par des affaires : %{items}",
        validating: "Validation\u2026",
        entities: {
          categories: "catégories",
          stages: "étapes",
        },
      },
    },
    voice_agent: {
      language: "Langue vocale",
    },
    bc_environment: {
      label: "Environnement BC principal",
    },
    theme: {
      dark: "Sombre",
      label: "Thème",
      light: "Clair",
      system: "Système",
    },
    language: "Langue",
    navigation: {
      label: "Navigation CRM",
    },
    mobile_nav: {
      mega_menu_title: "Menu",
      open_menu: "Ouvrir le menu",
      close_menu: "Fermer",
      my_tasks: "Mes tâches",
      section_main: "Principal",
      section_account: "Compte",
    },
    profile: {
      inbound: {
        description:
          "Vous pouvez commencer à envoyer des e-mails vers l'adresse de réception de votre serveur, par exemple en l'ajoutant au champ %{field}. Atomic CRM traitera les e-mails et ajoutera des notes aux contacts correspondants.",
        title: "E-mail entrant",
      },
      mcp: {
        title: "Serveur MCP",
        description:
          "Utilisez cette URL pour connecter votre assistant IA aux données de votre CRM via le Model Context Protocol (MCP).",
      },
      password: {
        change: "Changer le mot de passe",
      },
      password_reset_sent:
        "Un e-mail de réinitialisation du mot de passe a été envoyé à votre adresse e-mail",
      record_not_found: "Enregistrement introuvable",
      title: "Profil",
      updated: "Votre profil a été mis à jour",
      update_error: "Une erreur s'est produite. Veuillez réessayer",
    },
    validation: {
      invalid_url: "Doit être une URL valide",
      invalid_linkedin_url: "L'URL doit provenir de linkedin.com",
    },
    config: {
      deal_stages: {
        opportunity: "Opportunité",
        "proposal-sent": "Proposition envoyée",
        "in-negociation": "En négociation",
        won: "Gagnée",
        lost: "Perdue",
        delayed: "Reportée",
      },
      deal_categories: {
        other: "Autre",
        copywriting: "Rédaction",
        "print-project": "Projet d'impression",
        "ui-design": "Conception UI",
        "website-design": "Conception de site web",
      },
      note_statuses: {
        cold: "Froid",
        warm: "Tiède",
        hot: "Chaud",
        "in-contract": "Sous contrat",
      },
      task_types: {
        none: "Aucun",
        email: "E-mail",
        demo: "Démo",
        lunch: "Déjeuner",
        meeting: "Réunion",
        "follow-up": "Relance",
        "thank-you": "Remerciement",
        ship: "Expédition",
        call: "Appel",
      },
      company_sectors: {
        "communication-services": "Services de communication",
        "consumer-discretionary": "Consommation discrétionnaire",
        "consumer-staples": "Biens de consommation de base",
        energy: "Énergie",
        financials: "Finance",
        "health-care": "Santé",
        industrials: "Industrie",
        "information-technology": "Technologies de l'information",
        materials: "Matériaux",
        "real-estate": "Immobilier",
        utilities: "Services publics",
      },
    },
  },
} satisfies CrmMessages;
